import { apiClient } from "@/api/client";
import { endpoints } from "@/api/endpoints";
import type { ApiResponse } from "@/api/types";

export interface ErasureEligibility {
  canErase: boolean;
  /** Never shown raw to a client — they get "contact support" instead. */
  blockers: string[];
  /**
   * A7 — someone who has paid us: their identity stays on the payment records
   * for the statutory period; everything else is erased. The notice is the
   * server's, word for word, so the page and the outcome cannot disagree.
   */
  retention?: { applies: boolean; notice: string | null };
}

export interface EraseStartResult {
  sent: boolean;
  maskedPhone: string;
}

export interface SelfEraseInput {
  password: string;
  confirmEmail: string;
  code: string;
}

/** Mirrors ConsentService.status for a CONTACT. Dates arrive as ISO strings. */
export interface ConsentStatus {
  kind: "CONTACT";
  id: string;
  consent_given: boolean;
  consent_date: string | null;
  consent_source: string | null;
  consent_version: string | null;
  consent_withdrawn: boolean;
  consent_withdrawn_date: string | null;
  whatsapp_opt_in: boolean;
  whatsapp_opt_in_date: string | null;
}

/**
 * The client's own account settings, including the self-service erasure door.
 *
 * Every call acts on the signed-in client via their session — no id is ever
 * sent, so there is nothing here to point at somebody else.
 */
export const accountSettingsService = {
  async eligibility(): Promise<ErasureEligibility> {
    const { data } = await apiClient.get<ApiResponse<ErasureEligibility>>(
      endpoints.accountSettings.erasureEligibility,
    );
    return data.data;
  },

  /** Reversible. Nothing is destroyed. */
  async deactivate(password: string): Promise<{ deactivated: boolean }> {
    const { data } = await apiClient.post<ApiResponse<{ deactivated: boolean }>>(
      endpoints.accountSettings.deactivate,
      { password },
    );
    return data.data;
  },

  /** Sends the confirmation code to the registered mobile. */
  async startErase(): Promise<EraseStartResult> {
    const { data } = await apiClient.post<ApiResponse<EraseStartResult>>(
      endpoints.accountSettings.eraseStart,
      {},
    );
    return data.data;
  },

  /** No undo past this point. */
  async erase(input: SelfEraseInput): Promise<{ erased: boolean }> {
    const { data } = await apiClient.post<ApiResponse<{ erased: boolean }>>(
      endpoints.accountSettings.erase,
      input,
    );
    return data.data;
  },

  // NO dataExport() HERE. A client may view their data in the application and
  // may delete it, but may not download a machine-readable copy (owner,
  // 2026-08-21). The §6 access right is answered by staff through
  // /admin/data-rights/export, on the 30-day clock the privacy notice states.

  /** A18. What this client has consented to, and whether they have withdrawn. */
  async consent(): Promise<ConsentStatus> {
    const { data } = await apiClient.get<ApiResponse<ConsentStatus>>(
      endpoints.accountSettings.consent,
    );
    return data.data;
  },

  /**
   * Withdrawal is not erasure and the screen says so. It stops future
   * processing on a consent basis; it destroys nothing, and it revokes every
   * live share link for this person in the same transaction.
   */
  async withdrawConsent(reason?: string): Promise<ConsentStatus> {
    const { data } = await apiClient.post<ApiResponse<ConsentStatus>>(
      endpoints.accountSettings.consentWithdraw,
      reason ? { reason } : {},
    );
    return data.data;
  },
};
